import type React from 'react'
import { useState, useEffect, useCallback, useRef } from 'react'
import {
  MessageSquare, Search, X, FolderOpen,
  Star, MoreHorizontal, Pencil, Archive, Trash2, LogOut, PanelLeftClose,
  ChevronDown, ChevronRight, SquarePen, ArrowLeft, SlidersHorizontal,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { getToken } from '@/lib/auth'
import { useAppStore } from '@/store'
import { SessionOutcomeGate } from './SessionOutcomeGate'
import type { ChatMessage } from '@/types'

interface WorkspaceSession {
  id: string
  title: string
  project_id: string | null
  created_at: string
  updated_at: string
  archived: number
  message_count: number
  last_message: string | null
  favourite: number
}

interface Project {
  id: string
  name: string
  description: string | null
  status: string
}

const INIT_MSG: ChatMessage = {
  id: 'msg_init',
  role: 'system',
  content: 'Connected to Ops-Bridge. Live endpoint mode.',
  timestamp: new Date(),
}

function apiFetch(path: string, init?: RequestInit) {
  return fetch(`/api/dashboard${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
      ...((init?.headers as Record<string, string>) ?? {}),
    },
  }).then(r => r.json())
}

function getGroupLabel(iso: string): string {
  const d = new Date(iso)
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1)
  const week7 = new Date(today); week7.setDate(today.getDate() - 7)
  const week30 = new Date(today); week30.setDate(today.getDate() - 30)
  if (d >= today) return 'Today'
  if (d >= yesterday) return 'Yesterday'
  if (d >= week7) return 'Last 7 days'
  if (d >= week30) return 'Last 30 days'
  return 'Older'
}

const GROUP_ORDER = ['Today', 'Yesterday', 'Last 7 days', 'Last 30 days', 'Older']

function formatSessionMeta(s: WorkspaceSession): string {
  const updated = new Date(s.updated_at)
  const today = new Date()
  const sameDay = updated.toDateString() === today.toDateString()
  const stamp = sameDay
    ? `Today, ${updated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : updated.toLocaleDateString([], { month: 'short', day: 'numeric', year: updated.getFullYear() === today.getFullYear() ? undefined : 'numeric' })
  return s.message_count > 0 ? `${stamp} · ${s.message_count} messages` : stamp
}

// ── Create Project Modal ──────────────────────────────────────────────────────

interface CreateProjectModalProps {
  onClose: () => void
  onCreated: (project: Project, sessionId: string) => void
}

function CreateProjectModal({ onClose, onCreated }: CreateProjectModalProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function handleCreate() {
    const trimmed = name.trim()
    if (!trimmed) { setError('Project name is required'); return }
    setSaving(true)
    try {
      const pr = await apiFetch('/projects', {
        method: 'POST',
        body: JSON.stringify({ name: trimmed, description: description.trim() || undefined }),
      })
      if (!pr.success) { setError(pr.error || 'Failed to create project'); return }
      const sr = await apiFetch('/sessions', {
        method: 'POST',
        body: JSON.stringify({ title: trimmed, project_id: pr.project.id }),
      })
      if (!sr.success) { setError('Project created but session failed'); return }
      onCreated(pr.project, sr.session.id)
    } catch {
      setError('Network error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-border rounded-xl shadow-2xl w-full max-w-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-text-primary">New Workspace</h3>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-text-secondary block mb-1">Project name</label>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreate() }}
              placeholder="e.g. Mail Organiser Q3"
              className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent/50 transition-colors"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-text-secondary block mb-1">Description (optional)</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              placeholder="What is this workspace for?"
              className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent/50 transition-colors resize-none"
            />
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-3 py-2 rounded-lg border border-border text-xs text-text-muted hover:text-text-primary transition-colors">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={saving}
              className="flex-1 px-3 py-2 rounded-lg bg-accent text-white text-xs font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity"
            >
              {saving ? 'Creating…' : 'Create workspace'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Session Row ───────────────────────────────────────────────────────────────

interface SessionRowProps {
  s: WorkspaceSession
  isActive: boolean
  editingId: string | null
  editTitle: string
  menuOpenId: string | null
  menuRef: React.RefObject<HTMLDivElement | null>
  onSelect: () => void
  onEditStart: () => void
  onEditChange: (v: string) => void
  onEditCommit: () => void
  onEditCancel: () => void
  onMenuToggle: (e: React.MouseEvent) => void
  onFavourite: (e: React.MouseEvent) => void
  onArchive: (e: React.MouseEvent) => void
  onDelete: (e: React.MouseEvent) => void
  onEndSession: (e: React.MouseEvent) => void
}

function SessionRow({
  s, isActive, editingId, editTitle, menuOpenId, menuRef,
  onSelect, onEditStart, onEditChange, onEditCommit, onEditCancel,
  onMenuToggle, onFavourite, onArchive, onDelete, onEndSession,
}: SessionRowProps) {
  const isEditing = editingId === s.id
  const menuOpen = menuOpenId === s.id

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'ops-chat-row w-full text-left transition-all flex items-center gap-3 group',
          isActive ? 'ops-chat-row-active text-text-primary' : 'text-text-secondary'
        )}
      >
        <span className="ops-chat-icon"><MessageSquare className="h-5 w-5 shrink-0" /></span>
        {isEditing ? (
          <input
            autoFocus
            value={editTitle}
            onChange={e => onEditChange(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); onEditCommit() }
              if (e.key === 'Escape') onEditCancel()
            }}
            onBlur={onEditCommit}
            onClick={e => e.stopPropagation()}
            className="w-full bg-surface border border-accent/50 rounded px-2 py-0.5 text-sm text-text-primary focus:outline-none"
          />
        ) : (
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-[15px] font-medium text-text-primary truncate leading-snug flex-1">{s.title}</span>
                {s.favourite === 1 && <Star className="h-3 w-3 text-amber-400 shrink-0 fill-amber-400" />}
              </div>
              <span className="ops-chat-meta block truncate">{formatSessionMeta(s)}</span>
            </div>
            <button
              type="button"
              onClick={onMenuToggle}
              className="shrink-0 p-0.5 rounded text-text-muted hover:text-text-primary active:text-text-primary transition-colors"
              aria-label="Session menu"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </div>
        )}

      </button>

      {menuOpen && (
        <div
          ref={menuRef}
          className="absolute right-3 top-2 z-50 min-w-[140px] bg-surface-elevated border border-border rounded-lg shadow-lg py-1"
        >
          <button type="button" onClick={e => { e.stopPropagation(); onEditStart() }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-surface active:bg-surface transition-colors">
            <Pencil className="h-3 w-3" /> Rename
          </button>
          <button type="button" onClick={onFavourite}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-surface active:bg-surface transition-colors">
            <Star className={cn('h-3 w-3', s.favourite === 1 && 'fill-amber-400 text-amber-400')} />
            {s.favourite === 1 ? 'Unfavourite' : 'Favourite'}
          </button>
          <button type="button" onClick={onArchive}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-surface active:bg-surface transition-colors">
            <Archive className="h-3 w-3" /> Archive
          </button>
          <button type="button" onClick={onDelete}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-surface active:bg-surface transition-colors">
            <Trash2 className="h-3 w-3" /> Delete
          </button>
          {isActive && (
            <button type="button" onClick={onEndSession}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-warning hover:bg-surface active:bg-surface transition-colors border-t border-border mt-1">
              <LogOut className="h-3 w-3" /> End Session
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main Panel ────────────────────────────────────────────────────────────────

interface ChatsPanelProps {
  isMobile?: boolean
  onBackToSidebar?: () => void
}

export function ChatsPanel({ isMobile = false, onBackToSidebar }: ChatsPanelProps) {
  const {
    setSelectedChatSessionId,
    setActiveSessionId,
    setActiveProjectId,
    setChatMessages,
    activeSessionId,
    sessionListVersion,
    toggleChatPanel,
  } = useAppStore()

  const [sessions, setSessions] = useState<WorkspaceSession[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  // Collapsible date/project groups -- persisted per-browser so a collapsed
  // group stays collapsed across reloads, not just for the current session.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    try {
      const stored = window.localStorage.getItem('ops-bridge-collapsed-chat-groups')
      return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch { return new Set() }
  })
  const [creatingChat, setCreatingChat] = useState(false)
  const [outcomeSession, setOutcomeSession] = useState<WorkspaceSession | null>(null)
  const [showCreateProject, setShowCreateProject] = useState(false)

  const menuRef = useRef<HTMLDivElement | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    apiFetch(`/sessions?archived=${showArchived ? 'true' : 'false'}`)
      .then(r => setSessions(r.sessions ?? []))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false))
  }, [showArchived])

  useEffect(() => { load() }, [load])
  useEffect(() => { if (sessionListVersion > 0) load() }, [sessionListVersion, load])

  useEffect(() => {
    if (!menuOpenId) return
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpenId(null)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [menuOpenId])

  function handleSelect(s: WorkspaceSession) {
    if (editingId === s.id) return
    setMenuOpenId(null)
    setActiveSessionId(s.id)
    setSelectedChatSessionId(s.id)
    setActiveProjectId(s.project_id)
  }

  async function handleRename(id: string, title: string) {
    const trimmed = title.trim()
    if (!trimmed) { setEditingId(null); return }
    try {
      await apiFetch(`/sessions/${id}`, { method: 'PATCH', body: JSON.stringify({ title: trimmed }) })
      setSessions(prev => prev.map(s => s.id === id ? { ...s, title: trimmed } : s))
    } catch { /* silent */ }
    setEditingId(null)
  }

  async function handleFavourite(e: React.MouseEvent, s: WorkspaceSession) {
    e.stopPropagation(); setMenuOpenId(null)
    const newVal = s.favourite === 1 ? 0 : 1
    setSessions(prev => prev.map(x => x.id === s.id ? { ...x, favourite: newVal } : x))
    await apiFetch(`/sessions/${s.id}`, { method: 'PATCH', body: JSON.stringify({ favourite: newVal }) }).catch(() => {})
  }

  async function handleArchive(e: React.MouseEvent, id: string) {
    e.stopPropagation(); setMenuOpenId(null)
    await apiFetch(`/sessions/${id}`, { method: 'PATCH', body: JSON.stringify({ archived: true }) })
    if (activeSessionId === id) { setActiveSessionId(null); setSelectedChatSessionId(null); setActiveProjectId(null); setChatMessages([INIT_MSG]) }
    load()
  }

  async function handleDelete(e: React.MouseEvent, id: string) {
    e.stopPropagation(); setMenuOpenId(null)
    if (!confirm('Delete this chat? Messages will be lost.')) return
    await apiFetch(`/sessions/${id}`, { method: 'DELETE' })
    if (activeSessionId === id) { setActiveSessionId(null); setSelectedChatSessionId(null); setActiveProjectId(null); setChatMessages([INIT_MSG]) }
    load()
  }

  function handleEndSession(e: React.MouseEvent, s: WorkspaceSession) {
    e.stopPropagation(); setMenuOpenId(null); setOutcomeSession(s)
  }

  const filtered = sessions.filter(s => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return s.title.toLowerCase().includes(q) || (s.last_message ?? '').toLowerCase().includes(q)
  })

  const chats = filtered.filter(s => s.project_id === null)
  const chatGrouped = chats.reduce<Record<string, WorkspaceSession[]>>((acc, s) => {
    const g = getGroupLabel(s.updated_at); if (!acc[g]) acc[g] = []; acc[g].push(s); return acc
  }, {})

  function toggleGroup(key: string) {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      try { window.localStorage.setItem('ops-bridge-collapsed-chat-groups', JSON.stringify([...next])) } catch { /* ignore */ }
      return next
    })
  }

  async function handleNewChat() {
    if (creatingChat) return
    setCreatingChat(true)
    try {
      const r = await apiFetch('/sessions', { method: 'POST', body: JSON.stringify({ title: 'New Chat' }) })
      if (r.success && r.session) {
        setMenuOpenId(null)
        setActiveSessionId(r.session.id)
        setSelectedChatSessionId(r.session.id)
        setActiveProjectId(null)
        setChatMessages([INIT_MSG])
        load()
      }
    } catch { /* silent */ } finally { setCreatingChat(false) }
  }

  function rowProps(s: WorkspaceSession) {
    return {
      s, isActive: activeSessionId === s.id, editingId, editTitle, menuOpenId, menuRef,
      onSelect: () => handleSelect(s),
      onEditStart: () => { setMenuOpenId(null); setEditingId(s.id); setEditTitle(s.title) },
      onEditChange: (v: string) => setEditTitle(v),
      onEditCommit: () => handleRename(s.id, editTitle),
      onEditCancel: () => setEditingId(null),
      onMenuToggle: (e: React.MouseEvent) => { e.stopPropagation(); setMenuOpenId(prev => prev === s.id ? null : s.id) },
      onFavourite: (e: React.MouseEvent) => handleFavourite(e, s),
      onArchive: (e: React.MouseEvent) => handleArchive(e, s.id),
      onDelete: (e: React.MouseEvent) => handleDelete(e, s.id),
      onEndSession: (e: React.MouseEvent) => handleEndSession(e, s),
    }
  }

  return (
    <div className={cn('ops-chats-panel flex flex-col h-full overflow-hidden border-r border-border shrink-0', isMobile ? 'w-full' : 'w-[21rem]')}>
      {/* Chat-specific header replaces the dashboard top bar on mobile. */}
      <div className="ops-chats-header flex items-center gap-3 px-4 pt-4 pb-3 shrink-0">
        {isMobile && (
          <button type="button" onClick={onBackToSidebar} className="ops-chat-back" aria-label="Back to main menu">
            <ArrowLeft className="h-6 w-6" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="text-[22px] font-semibold text-text-primary leading-tight">Chats</h2>
          <p className="text-xs text-text-muted mt-0.5">OPS by BakersGuild</p>
        </div>
        <button
          type="button"
          onClick={handleNewChat}
          disabled={creatingChat}
          className="ops-new-chat flex items-center gap-2 disabled:opacity-60"
        >
          <SquarePen className="h-5 w-5" />
          <span>{creatingChat ? 'Creating…' : 'New chat'}</span>
        </button>
        {!isMobile && (
          <button type="button" onClick={toggleChatPanel} className="ops-chat-collapse" title="Collapse panel" aria-label="Collapse chat panel">
            <PanelLeftClose className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Search */}
      <div className="px-4 pb-3 shrink-0">
        <div className="ops-chat-search relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-text-muted pointer-events-none" />
          <input
            type="text"
            placeholder="Search chats..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-transparent pl-12 pr-12 py-3.5 text-[15px] text-text-primary placeholder:text-text-muted focus:outline-none"
          />
          {!search && <SlidersHorizontal className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted pointer-events-none" />}
          {search && (
            <button type="button" onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* List -- date-grouped chats only now; workspace sessions moved above */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 sidebar-scrollbar">
        {loading ? null : (
          <>
            {chats.length === 0 && !search.trim() ? (
              <div className="px-4 py-6 text-center">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 text-text-muted opacity-20" />
                <p className="text-xs text-text-muted">No chats yet</p>
              </div>
            ) : (
              GROUP_ORDER.filter(g => chatGrouped[g]?.length).map(group => {
                const groupKey = `date:${group}`
                const isCollapsed = collapsedGroups.has(groupKey)
                return (
                  <div key={group}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(groupKey)}
                      className="ops-chat-group w-full flex items-center gap-2 px-1.5 pt-4 pb-2 text-[13px] font-semibold text-text-secondary hover:text-text-primary transition-colors"
                    >
                      {isCollapsed ? <ChevronRight className="h-3 w-3 shrink-0" /> : <ChevronDown className="h-3 w-3 shrink-0" />}
                      {group}
                      <span className="opacity-60 font-normal normal-case">({chatGrouped[group].length})</span>
                    </button>
                    {!isCollapsed && chatGrouped[group].map(s => <SessionRow key={s.id} {...rowProps(s)} />)}
                  </div>
                )
              })
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="ops-chat-archive px-4 py-3 shrink-0">
        <button
          type="button"
          onClick={() => setShowArchived(a => !a)}
          className={cn('w-full flex items-center gap-3 text-[13px] font-medium transition-colors', showArchived ? 'text-warning' : 'text-text-secondary hover:text-text-primary')}
        >
          <FolderOpen className="h-3 w-3" />
          {showArchived ? 'Hide archived' : 'Show archived'}
        </button>
      </div>

      {showCreateProject && (
        <CreateProjectModal
          onClose={() => setShowCreateProject(false)}
          onCreated={(_project, sessionId) => {
            setShowCreateProject(false)
            setActiveSessionId(sessionId)
            setChatMessages([INIT_MSG])
            setSelectedChatSessionId(sessionId)
            load()
          }}
        />
      )}

      {outcomeSession && (
        <SessionOutcomeGate
          sessionId={outcomeSession.id}
          sessionTitle={outcomeSession.title}
          onClose={() => setOutcomeSession(null)}
          onComplete={(outcome) => {
            setOutcomeSession(null)
            if (outcome === 'discard') {
              apiFetch(`/sessions/${outcomeSession.id}`, { method: 'PATCH', body: JSON.stringify({ archived: true }) }).catch(() => {})
            }
            if (activeSessionId === outcomeSession.id) {
              setActiveSessionId(null); setSelectedChatSessionId(null); setActiveProjectId(null); setChatMessages([INIT_MSG])
            }
            load()
          }}
        />
      )}
    </div>
  )
}
